import { httpsCallable, getFunctions } from 'firebase/functions';
import { app } from './firebaseService';

export interface AISessionFetchRequest {
  locationId: string;
  action: "update" | "create";
  model: string;
  fields: string[];
  crawlDepth: number;
  pageCrawlMax: number;
  programKeys: string[];
  sourceUrl?: string;
  sourceContent?: string;
  sourceFile?: File;
  instructionalNotes?: string;
}

export interface SessionPreviewData {
  documentId?: string;
  programKey: string;
  startDate: string;
  endDate: string;
  [key: string]: any;
}

export interface AISessionFetchResponse {
  success: boolean;
  previewData: SessionPreviewData[];
  error?: string;
}

export interface AISessionApplyRequest {
  previewData: SessionPreviewData[];
  action: "update" | "create";
  locationId: string;
}

export interface AISessionApplyResponse {
  success: boolean;
  updatedCount?: number;
  createdCount?: number;
  /** KB-4101 — status values the model returned off-enum: not written (update) or written as unknown (create). */
  rejectedStatusValues?: number;
  error?: string;
}

class AISessionService {
  private functions: ReturnType<typeof getFunctions> | null = null;

  private getFunctionsInstance() {
    if (this.functions) return this.functions;
    try {
      this.functions = getFunctions(app, "us-east4");
      return this.functions;
    } catch (e) {
      console.warn("[AISessionService] Firebase Functions not initialized:", e);
      return null;
    }
  }

  async generatePreview(request: AISessionFetchRequest): Promise<AISessionFetchResponse> {
    try {
      console.log("[AISessionService] Generating preview with request:", request);

      const functions = this.getFunctionsInstance();
      if (!functions) {
        return {
          success: false,
          previewData: [],
          error: "Firebase Functions not initialized",
        };
      }
      
      // Convert File to base64 if present
      let fileData: string | undefined;
      if (request.sourceFile) {
        fileData = await this.fileToBase64(request.sourceFile);
      }

      const generatePreview = httpsCallable<any, AISessionFetchResponse>(
        functions, 
        'generateAIPoweredSessionPreview'
      );

      const response = await generatePreview({
        ...request,
        sourceFile: fileData ? {
          name: request.sourceFile?.name,
          type: request.sourceFile?.type,
          data: fileData
        } : undefined
      });

      console.log("[AISessionService] Preview generated successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("[AISessionService] Error generating preview:", error);
      return {
        success: false,
        previewData: [],
        error: error instanceof Error ? error.message : "Unknown error occurred"
      };
    }
  }

  async applyChanges(request: AISessionApplyRequest): Promise<AISessionApplyResponse> {
    try {
      console.log("[AISessionService] Applying changes with request:", request);

      const functions = this.getFunctionsInstance();
      if (!functions) {
        return {
          success: false,
          error: "Firebase Functions not initialized",
        };
      }

      const applyChanges = httpsCallable<AISessionApplyRequest, AISessionApplyResponse>(
        functions, 
        'applyAIPoweredSessionChanges'
      );

      const response = await applyChanges(request);

      console.log("[AISessionService] Changes applied successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("[AISessionService] Error applying changes:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred"
      };
    }
  }

  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data:mime;base64, prefix
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  }
}

export const aiSessionService = new AISessionService(); 